import type { ActionDialog, RuntimeConfigValueDraft } from '../types/dialogs';
import {
  MANAGED_RUNTIME_CONFIG_VALUE_KEYS,
  validateRuntimeConfigValue,
  validateRuntimeConfigValueKey
} from './runtimeConfigValueValidation';

type RuntimeConfigDraft = Extract<ActionDialog, { kind: 'runtime-config' }>;

type RuntimeConfigValueRows = {
  customRows: RuntimeConfigValueDraft[];
  filledValueRows: RuntimeConfigValueDraft[];
  hasInvalidValueRows: boolean;
};

export function buildRuntimeConfigValueRows(
  config: RuntimeConfigDraft
): RuntimeConfigValueRows {
  const dialogValues = config.values ?? [];
  const managedValueInputs: Array<
    Pick<
      RuntimeConfigValueDraft,
      'key' | 'value' | 'valueType' | 'masked' | 'valueTouched'
    >
  > = [
    {
      key: 'expressApiHostname',
      value: config.expressApiHostname.trim(),
      valueType: 'string',
      masked: false,
      valueTouched: true
    },
    {
      key: 'staticCdnPrefix',
      value: config.staticCdnPrefix.trim(),
      valueType: 'string',
      masked: false,
      valueTouched: true
    },
    {
      key: 'analyticsEnabled',
      value: String(config.analyticsEnabled),
      valueType: 'boolean',
      masked: false,
      valueTouched: true
    },
    {
      key: 'analyticsPageviewsEnabled',
      value: String(config.analyticsPageviewsEnabled),
      valueType: 'boolean',
      masked: false,
      valueTouched: true
    },
    {
      key: 'posthogApiKey',
      value: config.posthogApiKey.trim(),
      valueType: 'string',
      masked: false,
      valueTouched: true
    },
    {
      key: 'posthogHost',
      value: config.posthogHost.trim(),
      valueType: 'string',
      masked: false,
      valueTouched: true
    },
    {
      key: 'otelEnabled',
      value: String(config.otelEnabled),
      valueType: 'boolean',
      masked: false,
      valueTouched: true
    },
    {
      key: 'otelCollectorUrl',
      value: config.otelCollectorUrl.trim(),
      valueType: 'string',
      masked: false,
      valueTouched: true
    }
  ];
  const managedRows = managedValueInputs.map((input) => ({
    ...dialogValues.find((row) => row.key === input.key),
    ...input
  }));
  const customRows = dialogValues.filter(
    (row) => !MANAGED_RUNTIME_CONFIG_VALUE_KEYS.has(row.key)
  );
  const filledValueRows = [...managedRows, ...customRows].filter(
    (row) => row.key.trim() || row.value.trim()
  );
  const valueKeyCounts = new Map<string, number>();

  for (const row of filledValueRows) {
    const key = row.key.trim();
    if (key) valueKeyCounts.set(key, (valueKeyCounts.get(key) || 0) + 1);
  }

  const hasInvalidValueRows = filledValueRows.some((row) => {
    const key = row.key.trim();
    if (!key || validateRuntimeConfigValueKey(key)) return true;
    if ((valueKeyCounts.get(key) || 0) > 1) return true;
    return row.valueTouched
      ? Boolean(validateRuntimeConfigValue(row.value, row.valueType))
      : false;
  });

  return { customRows, filledValueRows, hasInvalidValueRows };
}
